import clsx from 'clsx';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import { formatNumber } from '@/lib/format';
import { PhaseShell, MatrixShell } from './ToolResultRendererSkeleton';

interface Sensitivity2DData {
  input1: { name: string; displayName: string; values: number[] };
  input2: { name: string; displayName: string; values: number[] };
  output: { name: string; displayName: string };
  matrix: number[][];
}

function isSensitivity2DResult(data: unknown): data is Sensitivity2DData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'input1' in data &&
    'input2' in data &&
    'matrix' in data &&
    Array.isArray((data as Sensitivity2DData).matrix)
  );
}

function getHeatColor(value: number, min: number, max: number): string {
  if (max === min) return 'bg-warn/30';
  const normalized = (value - min) / (max - min);
  if (normalized < 0.5) {
    const t = normalized * 2;
    if (t < 0.5) return 'bg-danger/40';
    return 'bg-warn/30';
  }
  const t = (normalized - 0.5) * 2;
  if (t < 0.5) return 'bg-warn/20';
  return 'bg-accent/20';
}

function getTextColor(value: number, min: number, max: number): string {
  if (max === min) return 'text-text';
  const normalized = (value - min) / (max - min);
  if (normalized < 0.25) return 'text-danger';
  if (normalized < 0.5) return 'text-warn';
  if (normalized < 0.75) return 'text-text-bright';
  return 'text-accent';
}

function renderComplete(result: Sensitivity2DData) {
  const { input1, input2, output, matrix } = result;
  let min = Infinity;
  let max = -Infinity;
  for (const row of matrix) {
    for (const val of row) {
      if (val < min) min = val;
      if (val > max) max = val;
    }
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-mono font-semibold text-accent tracking-[1.2px] uppercase">
        2D Sensitivity: {output.displayName}
      </div>
      <div className="text-xs font-mono text-text-muted">
        Rows: {input1.displayName} | Columns: {input2.displayName}
      </div>
      <div className="overflow-x-auto">
        <table className="border-collapse">
          <thead>
            <tr>
              <th className="text-xs font-mono font-semibold text-text-dim px-2 py-1 border-b border-panel-border">
                {input1.displayName} \ {input2.displayName}
              </th>
              {input2.values.map((v, i) => (
                <th key={i} className="text-center text-xs font-mono text-text-muted px-2 py-1 border-b border-panel-border tabular-nums">
                  {formatNumber(v, 2)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, ri) => (
              <tr key={ri} className="group">
                <td className="text-xs font-mono text-text-muted px-2 py-1 border-r border-panel-border tabular-nums group-hover:text-text-bright">
                  {formatNumber(input1.values[ri], 2)}
                </td>
                {row.map((val, ci) => (
                  <td
                    key={ci}
                    data-cell-painted="true"
                    className={clsx(
                      'heat-cell text-center text-xs font-mono px-2 py-1.5 tabular-nums border border-panel-border/30',
                      getHeatColor(val, min, max),
                      getTextColor(val, min, max),
                      'group-hover:brightness-110',
                    )}
                    title={`${input1.displayName}=${formatNumber(input1.values[ri], 2)}, ${input2.displayName}=${formatNumber(input2.values[ci], 2)} => ${formatNumber(val, 4)}`}
                  >
                    {formatNumber(val, 3)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Cell-by-cell paint while executing — Phase 4 emits `cell_computed` payloads. */
interface CellComputedPayload {
  x_idx: number;
  y_idx: number;
  output: Record<string, unknown>;
}

function renderInProgress(state: Extract<ToolCallState, { phase: 'executing' }>) {
  const input = state.input as Partial<{ x_axis: string; y_axis: string; dimensions: [number, number] }>;
  // Per directive §6.2 — `dimensions: [x_count, y_count]`. x is along columns,
  // y is along rows. Convert to [rows, cols] for table rendering.
  const [xCount, yCount] = input.dimensions ?? [5, 5];
  const [rows, cols] = [yCount, xCount];
  // Build a sparse paint map from progress events that match the cell_computed
  // shape. Phase 4 will emit these; Phase 2 ships the consumer code.
  const painted = new Map<string, number>();
  let min = Infinity;
  let max = -Infinity;
  for (const evt of state.progress) {
    if (evt.phase !== 'cell_computed') continue;
    const p = evt.payload as Partial<CellComputedPayload>;
    if (typeof p.x_idx !== 'number' || typeof p.y_idx !== 'number') continue;
    // Take the first numeric output value from the payload.output map.
    const outputMap = p.output as Record<string, unknown> | undefined;
    if (!outputMap) continue;
    const firstNumeric = Object.values(outputMap).find((v) => typeof v === 'number');
    if (typeof firstNumeric !== 'number') continue;
    painted.set(`${p.x_idx}-${p.y_idx}`, firstNumeric);
    if (firstNumeric < min) min = firstNumeric;
    if (firstNumeric > max) max = firstNumeric;
  }

  return (
    <PhaseShell variant="in-progress" title={`2D Sensitivity${state.input ? `: ${input.x_axis ?? '...'} × ${input.y_axis ?? '...'}` : ''}`}>
      <div className="overflow-x-auto">
        <table className="border-collapse">
          <tbody>
            {Array.from({ length: rows }).map((_, ri) => (
              <tr key={ri}>
                {Array.from({ length: cols }).map((_, ci) => {
                  const value = painted.get(`${ci}-${ri}`);
                  if (value === undefined) {
                    return (
                      <td
                        key={ci}
                        data-cell-painted="false"
                        className="px-2 py-1.5 border border-panel-border/30 text-center text-xs font-mono text-text-dim"
                      >
                        —
                      </td>
                    );
                  }
                  return (
                    <td
                      key={ci}
                      data-cell-painted="true"
                      className={clsx(
                        'px-2 py-1.5 border border-panel-border/30 text-center text-xs font-mono tabular-nums',
                        getHeatColor(value, min, max),
                        getTextColor(value, min, max),
                      )}
                    >
                      {formatNumber(value, 3)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PhaseShell>
  );
}

interface Sensitivity2DMatrixProps {
  state: ToolCallState;
}

/**
 * Phase-aware Sensitivity2DMatrix — directive §7.3 showcase.
 * - skeleton: empty matrix shell sized to `partialInput.dimensions ?? [5,5]`
 *   with axis labels as they stream.
 * - in-progress: cells paint in as `cell_computed` payloads accumulate in
 *   `state.progress[]`. Phase 4 emits the payloads; Phase 2 ships the consumer.
 * - complete: existing render.
 * - aborted: cells already painted stay; un-painted render as `—`.
 */
export function Sensitivity2DMatrix({ state }: Sensitivity2DMatrixProps) {
  if (state.phase === 'complete' && isSensitivity2DResult(state.result)) {
    return renderComplete(state.result);
  }
  if (state.phase === 'complete') {
    return (
      <div className="text-xs font-mono text-text-muted">
        <pre className="whitespace-pre-wrap">{JSON.stringify(state.result, null, 2)}</pre>
      </div>
    );
  }

  if (state.phase === 'executing') {
    return renderInProgress(state);
  }

  // input_streaming / input_ready / aborted → empty matrix shell.
  const inputData =
    state.phase === 'input_streaming' ? state.partialInput
    : state.phase === 'input_ready' ? state.input
    : state.phase === 'aborted' ? (state.input ?? {})
    : {};
  const probe = inputData as Partial<{ x_axis: string; y_axis: string; dimensions: [number, number] }>;
  // Directive §6.2 — `dimensions: [x_count, y_count]`. Convert to [rows, cols].
  const [xCount, yCount] = probe.dimensions ?? [5, 5];
  const [rows, cols] = [yCount, xCount];
  const variant = state.phase === 'aborted' ? 'aborted' : 'skeleton';
  return (
    <PhaseShell variant={variant} title="2D Sensitivity">
      <MatrixShell rows={rows} cols={cols} rowLabel={probe.x_axis} colLabel={probe.y_axis} />
    </PhaseShell>
  );
}
